/**
 * Cache de objetos `Style` do OpenLayers.
 *
 * Regra do módulo: nunca criar `Style` por feature a cada renderização. Cada
 * função de estilo tem o seu cache (por "classe" de feature: grupo, ordem,
 * nível…). Rótulos reaproveitam o mesmo `Style` trocando só o texto
 * (`style.getText().setText(...)`), padrão recomendado pelo OpenLayers — o
 * texto é copiado no momento do desenho, de forma síncrona.
 */
import type { FeatureLike } from 'ol/Feature';

export interface StyleCache<T> {
  /** Devolve o valor da chave, criando-o com `factory` na primeira vez. */
  get(key: string, factory: () => T): T;
  readonly size: number;
  clear(): void;
}

/** Cache com limite de entradas (descarta a mais antiga — LRU simples). */
export function createStyleCache<T>(maxEntries = 256): StyleCache<T> {
  const map = new Map<string, T>();
  return {
    get(key, factory) {
      const hit = map.get(key);
      if (hit !== undefined) {
        // Renova a posição (LRU).
        map.delete(key);
        map.set(key, hit);
        return hit;
      }
      const value = factory();
      map.set(key, value);
      if (map.size > maxEntries) {
        const oldest = map.keys().next().value;
        if (oldest !== undefined) map.delete(oldest);
      }
      return value;
    },
    get size() {
      return map.size;
    },
    clear() {
      map.clear();
    },
  };
}

/**
 * Memoiza um valor derivado da geometria de uma feature (ex.: ponto do
 * rótulo, área), recalculando só quando a geometria é trocada ou alterada.
 */
export function createGeometryMemo<V>(compute: (feature: FeatureLike) => V) {
  const memo = new WeakMap<FeatureLike, { geometry: unknown; rev: number; value: V }>();
  return (feature: FeatureLike): V => {
    const geometry = feature.getGeometry();
    const rev = geometry && 'getRevision' in geometry ? geometry.getRevision() : 0;
    const hit = memo.get(feature);
    if (hit && hit.geometry === geometry && hit.rev === rev) return hit.value;
    const value = compute(feature);
    memo.set(feature, { geometry, rev, value });
    return value;
  };
}
